import {Component, Inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import * as moment from 'moment';
import {HomeService} from '../_services/home.service';
import {DOCUMENT} from '@angular/common';
import {
  DEFAULT_DRIVING_SCHOOL,
  DrivingSchool,
  DrivingSchoolFormula,
  getDrivingSchoolByHostname,
  getDrivingSchoolFormulas
} from '../driving-school.config';

declare let $: any;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent {
  initStudentForm = this.fb.group({
    sexe: ['F', Validators.required],
    formulaId: [null, Validators.required],
    neph: [null],
    firstname: [null, Validators.required],
    lastname: [null, Validators.required],
    email: [null, [Validators.required, Validators.email]],
    birthDate: [null, Validators.required],
    birthPlace: [null, Validators.required],
    phoneNumber: [null, [Validators.required, Validators.pattern('0[0-9]{9}')]],
    address: [null, Validators.required],
    postalCode: [null, Validators.required],
    city: [null, Validators.required],
    contractDate: [null],
    settlementDate: [null],
    registrationDate: [this.getToday(), Validators.required],
    hasCode: [null]
  });

  school: DrivingSchool = DEFAULT_DRIVING_SCHOOL;
  formulas: DrivingSchoolFormula[] = [];
  messageError = '';
  submited = false;
  sendValidated = false;
  sending = false;

  readonly heroHighlights = [
    {
      title: 'Permis B',
      description: 'Une formation complète et de qualité'
    },
    {
      title: 'Code en ligne',
      description: 'Entraînez-vous à votre rythme'
    },
    {
      title: 'Accompagnement',
      description: 'Un suivi jusqu’à l’examen'
    }
  ];

  readonly requiredDocuments = [
    'Pièce d’identité',
    'E-photo (photo permis)',
    '2 enveloppes A5 lettre suivie',
    'Justificatif de domicile de moins de 3 mois',
    'Attestation d’hébergement et pièce d’identité de l’hébergeant si besoin',
    'JDC pour les moins de 25 ans'
  ];

  constructor(private fb: UntypedFormBuilder,
              private homeService: HomeService,
              @Inject(DOCUMENT) private document: Document) {
    const hostname = this.document.location.hostname;
    this.school = getDrivingSchoolByHostname(hostname);
    this.formulas = getDrivingSchoolFormulas(hostname);
  }

  get fullAddress(): string {
    return `${this.school.addressLine}, ${this.school.postalCode} ${this.school.city}`;
  }

  get heroImagePath(): string {
    return this.school.heroImagePath;
  }

  get heroImageAlt(): string {
    return this.school.heroImageAlt;
  }

  get selectedFormula(): DrivingSchoolFormula | undefined {
    const formulaId = this.initStudentForm.get('formulaId')?.value;

    return this.formulas.find((formula) => formula.id === formulaId);
  }

  selectFormula(formulaId: number | string): void {
    this.initStudentForm.patchValue({formulaId});
    this.initStudentForm.get('formulaId')?.markAsTouched();

    this.document.getElementById('pre-inscription')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  isFormulaSelected(formulaId: number | string): boolean {
    return this.initStudentForm.get('formulaId')?.value === formulaId;
  }

  trackByFormulaId(index: number, formula: DrivingSchoolFormula): number | string {
    return formula.id;
  }

  isSubmited(form: UntypedFormGroup): void {
    this.submited = true;
    this.messageError = '';

    if (!form.valid || this.sending) {
      return;
    }

    this.sending = true;

    this.homeService.initStudent(form.value).subscribe(
      () => {
        this.sending = false;
        this.submited = false;
        this.sendValidated = true;
        this.resetInitStudentForm();
        $('#modal-send-validated').modal('show');
      },
      err => {
        this.sending = false;
        this.messageError = err?.error?.message || 'Une erreur est survenue lors de l’envoi de la pré-inscription.';
      }
    );
  }

  private resetInitStudentForm(): void {
    this.initStudentForm.reset({
      sexe: 'F',
      formulaId: null,
      neph: null,
      firstname: null,
      lastname: null,
      email: null,
      birthDate: null,
      birthPlace: null,
      phoneNumber: null,
      address: null,
      postalCode: null,
      city: null,
      contractDate: null,
      settlementDate: null,
      registrationDate: this.getToday(),
      hasCode: null
    });
  }

  private getToday(): string {
    return moment(new Date()).format('YYYY-MM-DD');
  }
}
